import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
	ConnectComponentsProvider,
	ConnectAccountOnboarding,
} from '@stripe/react-connect-js';
import {
	BuildingLibraryIcon,
	CreditCardIcon,
	LockClosedIcon,
	BoltIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Spinner } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import useStaffProfileStore from '../../stores/staffProfileStore';
import type { PaymentStatus, SubscriptionStatus, ConnectOnboardingStatus } from '../../types/payment';
import type { StaffPricingTier } from '../../types/staff';

// ---- Map backend subscription status to UI payment status ----
const mapSubscriptionStatus = (backendStatus: SubscriptionStatus): PaymentStatus => {
	switch (backendStatus) {
		case 'NONE':
			return 'not_connected';
		case 'PENDING_VERIFICATION':
			return 'pending_verification';
		case 'CANCELLED':
			return 'cancelled';
		default:
			return 'verified';
	}
};

// ---- Progress Stepper (2 steps: Connect Bank, Complete) ----
const STEPS = [
	{ id: 1, label: 'Connect Bank' },
	{ id: 2, label: 'Complete' },
];

const ProgressStepper = ({ currentStep }: { currentStep: number }) => (
	<div className="payment-stepper">
		{STEPS.map((step, index) => (
			<div key={step.id} className="payment-stepper__step">
				{index > 0 && (
					<div
						className={`payment-stepper__connector ${
							step.id <= currentStep ? 'payment-stepper__connector--active' : ''
						}`}
					/>
				)}
				<div
					className={`payment-stepper__circle ${
						step.id < currentStep
							? 'payment-stepper__circle--completed'
							: step.id === currentStep
								? 'payment-stepper__circle--current'
								: 'payment-stepper__circle--upcoming'
					}`}
				>
					{step.id < currentStep ? (
						<CheckCircleIcon className="w-5 h-5" />
					) : (
						<span>{step.id}</span>
					)}
				</div>
				<span
					className={`payment-stepper__label ${
						step.id === currentStep ? 'payment-stepper__label--active' : ''
					}`}
				>
					{step.label}
				</span>
			</div>
		))}
	</div>
);

// ---- Status helpers ----
const getStatusBadge = (status: PaymentStatus, subscriptionStatus?: string) => {
	switch (status) {
		case 'not_connected':
			return <Badge variant="warning">Not Connected</Badge>;
		case 'pending_verification':
			return <Badge variant="info">Pending Verification</Badge>;
		case 'cancelled':
			return <Badge variant="danger">Cancelled</Badge>;
		case 'verified':
			return (
				<Badge variant="success">
					{subscriptionStatus === 'ACTIVE' ? 'Active' : 'Verified'}
				</Badge>
			);
	}
};

const getStepFromStatus = (status: PaymentStatus, subscriptionStatus?: string): number => {
	switch (status) {
		case 'not_connected':
			return 1;
		case 'pending_verification':
			return 1;
		case 'cancelled':
			return 2;
		case 'verified':
			return subscriptionStatus === 'ACTIVE' ? 3 : 2;
	}
};

// ---- Pricing Tier Card ----
const PricingTierCard = ({ tiers }: { tiers: StaffPricingTier[] }) => {
	if (!tiers || tiers.length === 0) return null;

	const formatTierLabel = (tier: StaffPricingTier): string => {
		if (tier.monthTo === null) return `Month ${tier.monthFrom}+`;
		if (tier.monthFrom === tier.monthTo) return `Month ${tier.monthFrom}`;
		return `Month ${tier.monthFrom}–${tier.monthTo}`;
	};

	const formatFee = (fee: number): string => {
		if (fee === 0) return 'FREE';
		return `$${fee.toFixed(2)}/mo`;
	};

	return (
		<div
			className="rounded-xl p-5 mb-6 text-white"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<p className="text-sm opacity-90 mb-1">Monthly Platform Fee</p>
			<p className="text-3xl font-bold mb-1">
				{formatFee(tiers[tiers.length - 1]?.fee ?? 0)}
			</p>
			<p className="text-xs opacity-70 mb-5">Billed monthly on the 1st</p>

			<div className="flex flex-col gap-2">
				{tiers.map((tier, i) => (
					<div
						key={i}
						className="flex items-center justify-between rounded-lg px-4 py-2.5"
						style={{ background: 'rgba(255,255,255,0.15)' }}
					>
						<span className="text-sm font-medium opacity-90">
							{formatTierLabel(tier)}
						</span>
						<span className={`text-sm font-bold ${tier.fee === 0 ? 'text-emerald-300' : ''}`}>
							{formatFee(tier.fee)}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

// ---- Verified / Cancelled View ----
const BankAccountStatusView = ({
	onCancelSubscription,
	onResumeSubscription,
	isCancelling,
	isResuming,
	subscriptionStatus,
}: {
	onCancelSubscription: () => void;
	onResumeSubscription: () => void;
	isCancelling: boolean;
	isResuming: boolean;
	subscriptionStatus: string;
}) => {
	const navigate = useNavigate();
	const isCancelled = subscriptionStatus === 'CANCELLED';

	return (
		<div className="payment-setup__success">
			<div className={`payment-setup__success-icon ${isCancelled ? 'payment-setup__success-icon--cancelled' : ''}`}>
				{isCancelled ? (
					<ExclamationTriangleIcon className="w-10 h-10 text-amber-500" />
				) : (
					<CheckCircleIcon className="w-10 h-10" />
				)}
			</div>
			<h3 className="payment-setup__title">
				{isCancelled ? 'Subscription Cancelled' : 'Payment Setup Complete!'}
			</h3>
			<p className="payment-setup__description">
				{isCancelled
					? 'Your subscription has been cancelled. Your bank account is still on file.'
					: 'Your bank account has been verified successfully.'}
			</p>

			<div className="payment-setup__bank-details">
				<div className="payment-setup__bank-row">
					<span className="payment-setup__bank-label">Payout Schedule</span>
					<span className="payment-setup__bank-value">Monthly</span>
				</div>
				<div className="payment-setup__bank-row">
					<span className="payment-setup__bank-label">Status</span>
					<span className={`payment-setup__bank-value ${isCancelled ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
						{isCancelled ? 'Cancelled' : subscriptionStatus === 'ACTIVE' ? 'Active' : 'Verified'}
					</span>
				</div>
			</div>

			<div className="payment-setup__actions payment-setup__actions--center">
				{isCancelled ? (
					<Button
						variant="primary"
						size="lg"
						onClick={onResumeSubscription}
						isLoading={isResuming}
					>
						{isResuming ? 'Resuming...' : 'Resume Subscription'}
					</Button>
				) : (
					<>
						<Button
							variant="primary"
							size="lg"
							onClick={() => navigate('/staff/dashboard')}
						>
							Go to Dashboard
						</Button>
						{subscriptionStatus === 'ACTIVE' && (
							<Button
								variant="secondary"
								size="lg"
								onClick={onCancelSubscription}
								isLoading={isCancelling}
							>
								{isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
							</Button>
						)}
					</>
				)}
			</div>
		</div>
	);
};

// ---- Automatic Payouts Card (Stripe Connect Embedded Onboarding) ----
const AutomaticPayoutsCard = ({
	isBankConnected,
	initialConnectStatus,
	onRefreshProfile,
}: {
	isBankConnected: boolean;
	initialConnectStatus: ConnectOnboardingStatus;
	onRefreshProfile: () => Promise<void>;
}) => {
	const [connectStatus, setConnectStatus] = useState<ConnectOnboardingStatus>(initialConnectStatus);
	const [stripeConnectInstance, setStripeConnectInstance] = useState<ReturnType<typeof loadConnectAndInitialize> | null>(null);
	const [isStartingOnboarding, setIsStartingOnboarding] = useState(false);
	const [isDisconnecting, setIsDisconnecting] = useState(false);
	const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

	// Sync with profile changes
	useEffect(() => {
		setConnectStatus(initialConnectStatus);
	}, [initialConnectStatus]);

	// Start Stripe Connect embedded onboarding
	const handleStartOnboarding = async () => {
		if (!isBankConnected) {
			toast.error('Please connect and verify your bank account first before setting up automatic payouts.');
			return;
		}

		setIsStartingOnboarding(true);
		try {
			// Fetch publishable key from connect status API
			const connectStatus = await staffProfileApi.getStripeConnectStatus();
			const publishableKey = connectStatus.stripePublishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

			const connectInstance = loadConnectAndInitialize({
				publishableKey,
				fetchClientSecret: async () => {
					const response = await staffProfileApi.createConnectAccountSession();
					return response.clientSecret;
				},
			});

			setStripeConnectInstance(connectInstance);
			setConnectStatus('PENDING');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to start Stripe onboarding');
		} finally {
			setIsStartingOnboarding(false);
		}
	};

	// Handle onboarding completion
	const handleOnboardingExit = async () => {
		await onRefreshProfile();
		toast.success('Stripe onboarding completed! Verifying your account...');
	};

	// Status badge for the card header
	const getConnectBadge = () => {
		switch (connectStatus) {
			case 'COMPLETE':
				return (
					<span className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
						Active
					</span>
				);
			case 'PENDING':
				return (
					<span className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
						Onboarding Pending
					</span>
				);
			default:
				return (
					<span className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
						Not Started
					</span>
				);
		}
	};

	return (
		<div className="settings-card mb-6">
			<div className="settings-card__header">
				<div
					className="settings-card__icon"
					style={{ background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}
				>
					<BoltIcon className="w-6 h-6 text-purple-600" />
				</div>
				<div className="flex-1">
					<h2 className="settings-card__title">Automatic Payouts</h2>
					<p className="settings-card__subtitle">
						Connect your Stripe account to receive earnings directly to your bank
					</p>
				</div>
				{getConnectBadge()}
			</div>
			<div className="settings-card__body">
				{connectStatus === 'NOT_STARTED' ? (
					<>
						{/* How it works */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
							<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-center">
								<div className="text-3xl mb-2">
									<BuildingLibraryIcon className="w-8 h-8 mx-auto text-purple-500" />
								</div>
								<p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
									1. Connect via Stripe
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
									Complete Stripe&apos;s embedded onboarding — you never leave BBP
								</p>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-center">
								<div className="text-3xl mb-2">
									<CheckCircleIcon className="w-8 h-8 mx-auto text-emerald-500" />
								</div>
								<p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
									2. Stripe Verifies You
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
									Stripe handles KYC, identity and bank validation — no PCI burden on BBP
								</p>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-center">
								<div className="text-3xl mb-2">
									<CreditCardIcon className="w-8 h-8 mx-auto text-amber-500" />
								</div>
								<p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
									3. Earn Automatically
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
									Your share is auto-deposited after each customer payment
								</p>
							</div>
						</div>

						{/* Fee breakdown preview */}
						<div className="rounded-xl p-5 mb-6 border" style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', borderColor: '#ddd6fe' }}>
							<p className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider mb-3.5">
								How Your Earnings Are Calculated
							</p>
							<div className="grid grid-cols-2 gap-4 text-sm">
								<div>
									<p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Customer pays (example)</p>
									<p className="font-bold text-gray-900 dark:text-white text-base">$113.45</p>
									<p className="text-gray-400 dark:text-gray-500 text-[10px]">incl. 3% + $0.35 transaction fee</p>
								</div>
								<div>
									<p className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">BBP platform fee</p>
									<p className="font-bold text-red-600 dark:text-red-400 text-base">&minus; $1.85</p>
									<p className="text-gray-400 dark:text-gray-500 text-[10px]">2% + $0.35 (configurable)</p>
								</div>
								<div className="col-span-2 border-t border-purple-200 dark:border-purple-800 pt-3 flex justify-between items-center">
									<span className="font-bold text-gray-900 dark:text-white">You receive</span>
									<span className="font-extrabold text-emerald-600 dark:text-emerald-400 text-lg">$111.60</span>
								</div>
							</div>
						</div>

						{/* CTA Button — disabled until bank is connected */}
						<Button
							variant="primary"
							size="lg"
							onClick={handleStartOnboarding}
							isLoading={isStartingOnboarding}
							disabled={!isBankConnected}
							className={`w-full ${!isBankConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
						>
							<span className="flex items-center justify-center gap-2">
								<BoltIcon className="w-5 h-5" />
								Set Up Automatic Payouts via Stripe
							</span>
						</Button>
						<p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2.5">
							Powered by Stripe Connect · Your bank details are handled entirely by Stripe
						</p>
					</>
				) : connectStatus === 'PENDING' ? (
					<>
						{/* Onboarding in progress banner */}
						<div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
							<ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0" />
							<div>
								<p className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
									Onboarding In Progress
								</p>
								<p className="text-xs text-amber-700 dark:text-amber-300">
									Complete the form below to finish verifying your identity and bank account with Stripe.
								</p>
							</div>
						</div>

						{/* Stripe Embedded Onboarding Component */}
						{stripeConnectInstance ? (
							<ConnectComponentsProvider connectInstance={stripeConnectInstance}>
								<ConnectAccountOnboarding onExit={handleOnboardingExit} />
							</ConnectComponentsProvider>
						) : (
							<div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center mb-6">
								<LockClosedIcon className="w-14 h-14 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
								<p className="font-bold text-gray-900 dark:text-white text-sm mb-2">
									Stripe Identity & Bank Verification
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mx-auto mb-5">
									The Stripe embedded onboarding form will appear here. Stripe collects your business information,
									identity documents, and bank details — BBP never sees this data.
								</p>
								<Button
									variant="primary"
									size="md"
									onClick={handleStartOnboarding}
									isLoading={isStartingOnboarding}
								>
									Continue in Stripe
								</Button>
							</div>
						)}

						<button
							onClick={() => setConnectStatus('NOT_STARTED')}
							className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
						>
							&larr; Cancel and start over
						</button>
					</>
				) : (
					/* COMPLETE state */
					<>
						{/* Success banner */}
						<div className="flex items-center gap-4 rounded-xl p-5 mb-6 border-[1.5px]" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderColor: '#86efac' }}>
							<div className="w-13 h-13 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', width: 52, height: 52 }}>
								<CheckCircleIcon className="w-7 h-7 text-white" />
							</div>
							<div className="flex-1">
								<p className="font-bold text-green-800 dark:text-green-200 text-sm mb-0.5">
									Stripe Connect — Active
								</p>
								<p className="text-xs text-green-700 dark:text-green-300 leading-relaxed">
									Your Stripe account is verified and payouts are enabled. Customer payments will be automatically split and deposited to your bank.
								</p>
							</div>
						</div>

						{/* Connected account details */}
						<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-5 mb-6">
							<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
								Connected Account
							</p>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
									<div className="flex items-center gap-1.5">
										<span className="w-2 h-2 bg-emerald-500 rounded-full inline-block" />
										<span className="font-semibold text-gray-900 dark:text-white text-sm">Connected</span>
									</div>
								</div>
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Payouts</p>
									<span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">
										Enabled
									</span>
								</div>
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">KYC / Identity</p>
									<span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">
										Verified
									</span>
								</div>
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Payout Method</p>
									<span className="font-semibold text-gray-900 dark:text-white text-sm">STRIPE_CONNECT</span>
								</div>
							</div>
						</div>

						{/* Payment routing diagram */}
						<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl p-5 mb-6">
							<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
								How Payments Are Routed
							</p>
							<div className="flex items-center gap-2 flex-wrap">
								<div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center flex-1 min-w-[100px]">
									<BuildingLibraryIcon className="w-6 h-6 mx-auto text-blue-600 dark:text-blue-400 mb-1" />
									<p className="text-xs font-semibold text-blue-800 dark:text-blue-200">Customer Pays</p>
								</div>
								<span className="text-gray-400 font-bold text-lg">&rarr;</span>
								<div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center flex-1 min-w-[100px]">
									<BoltIcon className="w-6 h-6 mx-auto text-purple-600 dark:text-purple-400 mb-1" />
									<p className="text-xs font-semibold text-purple-800 dark:text-purple-200">Stripe Splits</p>
								</div>
								<span className="text-gray-400 font-bold text-lg">&rarr;</span>
								<div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center flex-1 min-w-[100px]">
									<CreditCardIcon className="w-6 h-6 mx-auto text-amber-600 dark:text-amber-400 mb-1" />
									<p className="text-xs font-semibold text-amber-800 dark:text-amber-200">BBP Fee</p>
								</div>
								<span className="text-gray-400 font-bold text-lg">+</span>
								<div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-center flex-1 min-w-[100px]">
									<BuildingLibraryIcon className="w-6 h-6 mx-auto text-emerald-600 dark:text-emerald-400 mb-1" />
									<p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">Your Bank</p>
								</div>
							</div>
						</div>

						{/* Disconnect button */}
						<button
							onClick={() => setShowDisconnectConfirm(true)}
							disabled={isDisconnecting}
							className="text-sm font-semibold text-red-500 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
						>
							{isDisconnecting && <Spinner size="sm" />}
							{isDisconnecting ? 'Disconnecting...' : 'Disconnect Stripe Account'}
						</button>

						{/* Disconnect Confirmation Dialog */}
						<ConfirmDialog
							isOpen={showDisconnectConfirm}
							title="Disconnect Stripe Account"
							message="Are you sure you want to disconnect your Stripe account? You will no longer receive automatic payouts from customer payments."
							confirmLabel="Yes, Disconnect"
							cancelLabel="Keep Connected"
							loadingLabel="Disconnecting..."
							isLoading={isDisconnecting}
							onConfirm={async () => {
								setIsDisconnecting(true);
								try {
									await staffProfileApi.disconnectStripeAccount();
									setConnectStatus('NOT_STARTED');
									setShowDisconnectConfirm(false);
									toast.success('Stripe account disconnected successfully');
								} catch (err) {
									const apiError = err as ApiError;
									toast.error(apiError.message || 'Failed to disconnect Stripe account');
								} finally {
									setIsDisconnecting(false);
								}
							}}
							onCancel={() => setShowDisconnectConfirm(false)}
						/>
					</>
				)}
			</div>
		</div>
	);
};

// ---- Confirmation Dialog ----
const ConfirmDialog = ({
	isOpen,
	title,
	message,
	confirmLabel,
	cancelLabel = 'Cancel',
	loadingLabel = 'Processing...',
	isLoading,
	onConfirm,
	onCancel,
}: {
	isOpen: boolean;
	title: string;
	message: string;
	confirmLabel: string;
	cancelLabel?: string;
	loadingLabel?: string;
	isLoading: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div className="fixed inset-0 bg-black/50" onClick={onCancel} />
			<div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
				<div className="flex items-start gap-4 mb-4">
					<div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
						<ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
					</div>
					<div>
						<h3 className="font-bold text-gray-900 dark:text-white text-lg">{title}</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{message}</p>
					</div>
				</div>
				<div className="flex gap-3 justify-end mt-6">
					<Button variant="secondary" onClick={onCancel} disabled={isLoading}>
						{cancelLabel}
					</Button>
					<button
						onClick={onConfirm}
						disabled={isLoading}
						className="px-4 py-2 rounded-lg font-semibold text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
					>
						{isLoading ? loadingLabel : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

// ---- Main Page Component ----
const StaffPaymentSetup = () => {
	const { profile, isLoadingProfile, fetchProfile } = useStaffProfileStore();
	const [status, setStatus] = useState<PaymentStatus>('not_connected');
	const [isConnecting, setIsConnecting] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const [isResuming, setIsResuming] = useState(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);

	// Derive status from profile's subscriptionStatus
	useEffect(() => {
		if (profile) {
			setStatus(mapSubscriptionStatus(profile.subscriptionStatus));
		}
	}, [profile]);

	// Handle instant bank verification via Stripe Financial Connections
	const handleConnectBank = async () => {
		if (!profile) return;

		setIsConnecting(true);
		try {
			// Step 1: Call backend to create SetupIntent
			const response = await staffProfileApi.setupPayment();
			const { clientSecret: secret, stripePublishableKey } = response;

			// Step 2: Load Stripe.js
			const stripe = await loadStripe(
				stripePublishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
			);
			if (!stripe) {
				toast.error('Failed to load payment processor');
				return;
			}

			// Step 3: Open Stripe Financial Connections modal
			const { setupIntent, error: collectError } =
				await stripe.collectBankAccountForSetup({
					clientSecret: secret,
					params: {
						payment_method_type: 'us_bank_account',
						payment_method_data: {
							billing_details: {
								name: `${profile.firstName} ${profile.lastName}`,
							},
						},
					},
				});

			if (collectError) {
				toast.error(collectError.message || 'Failed to connect bank account');
				return;
			}

			if (!setupIntent) {
				toast.error('Bank account connection was cancelled');
				return;
			}

			// Step 4: If requires_confirmation, confirm the setup
			if (setupIntent.status === 'requires_confirmation') {
				const { setupIntent: confirmedIntent, error: confirmError } =
					await stripe.confirmUsBankAccountSetup(secret);

				if (confirmError) {
					toast.error(confirmError.message || 'Failed to confirm bank account');
					return;
				}

				if (confirmedIntent?.status === 'succeeded') {
					await fetchProfile();
					toast.success('Bank account connected and verified!');
					return;
				}
			}

			if (setupIntent.status === 'succeeded') {
				await fetchProfile();
				toast.success('Bank account connected and verified!');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to connect bank account');
		} finally {
			setIsConnecting(false);
		}
	};

	// Handle resume subscription
	const handleResumeSubscription = async () => {
		if (!profile) return;

		setIsResuming(true);
		try {
			await staffProfileApi.resumeSubscription();
			await fetchProfile();
			toast.success('Subscription resumed successfully!');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to resume subscription');
		} finally {
			setIsResuming(false);
		}
	};

	// Handle cancel subscription
	const handleCancelSubscription = async () => {
		if (!profile) return;

		setIsCancelling(true);
		try {
			await staffProfileApi.cancelSubscription();
			await fetchProfile();
			setShowCancelConfirm(false);
			toast.success('Subscription cancelled successfully');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to cancel subscription');
		} finally {
			setIsCancelling(false);
		}
	};

	if (isLoadingProfile && !isConnecting) {
		return (
			<AppLayout>
				<div className="py-16 text-center">
					<Spinner
						size="lg"
						className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
					/>
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
				</div>
			</AppLayout>
		);
	}

	const currentStep = getStepFromStatus(status, profile?.subscriptionStatus);
	const isBankConnected = status === 'verified' || (status === 'cancelled' && !!profile?.hasBankAccount);

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					Payment Setup
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Connect your bank account to start receiving payments
				</p>
			</div>

			{/* Progress Stepper */}
			<ProgressStepper currentStep={currentStep} />

			{/* Bank Account Card */}
			<div className="settings-card mb-6">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<BuildingLibraryIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Bank Account</h2>
						<p className="settings-card__subtitle">
							Connect and verify your bank account for payouts
						</p>
					</div>
					{getStatusBadge(status, profile?.subscriptionStatus)}
				</div>
				<div className="settings-card__body">
					{/* Not connected — show Connect Bank Account button */}
					{(status === 'not_connected' || status === 'pending_verification') && (
						<div className="payment-setup__bank-form">
							<div className="payment-setup__security-badge">
								<BuildingLibraryIcon className="w-8 h-8" />
							</div>
							<h3 className="payment-setup__title">Connect Your Bank Account</h3>
							<p className="payment-setup__description">
								Securely connect your bank account through Stripe to start receiving
								payouts from appointments. Your banking information is handled entirely
								by Stripe and never stored by BBP.
							</p>

							{/* Pricing Tiers */}
							{profile?.staffPricingConfig?.tiers && (
								<PricingTierCard tiers={profile.staffPricingConfig.tiers} />
							)}

							{/* Security note */}
							<div className="payment-setup__security-note mb-6">
								<LockClosedIcon className="w-4 h-4" />
								<span>
									Your payment information is encrypted and securely processed through
									Stripe. We never store or share your financial data.
								</span>
							</div>

							{/* Connect Button */}
							<div className="payment-setup__actions">
								<Button
									variant="primary"
									size="lg"
									onClick={handleConnectBank}
									isLoading={isConnecting}
								>
									{isConnecting ? (
										'Connecting...'
									) : (
										<span className="flex items-center gap-2">
											<BuildingLibraryIcon className="w-5 h-5" />
											Connect Bank Account
										</span>
									)}
								</Button>
							</div>
						</div>
					)}

					{/* Verified / Active / Cancelled — show status + actions */}
					{(status === 'verified' || status === 'cancelled') && (
						<>
						{profile?.staffPricingConfig?.tiers && (
							<PricingTierCard tiers={profile.staffPricingConfig.tiers} />
						)}
						<BankAccountStatusView
							onCancelSubscription={() => setShowCancelConfirm(true)}
							onResumeSubscription={handleResumeSubscription}
							isCancelling={isCancelling}
							isResuming={isResuming}
							subscriptionStatus={profile?.subscriptionStatus ?? 'VERIFIED'}
						/>
						</>
					)}
				</div>
			</div>

			{/* Automatic Payouts Card */}
			<AutomaticPayoutsCard
				isBankConnected={isBankConnected}
				initialConnectStatus={(profile?.connectOnboardingStatus as ConnectOnboardingStatus) || 'NOT_STARTED'}
				onRefreshProfile={fetchProfile}
			/>

			{/* Billing Details Card — hidden until fee details are added to staff profile */}

			{/* Cancel Subscription Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showCancelConfirm}
				title="Cancel Subscription"
				message="Are you sure you want to cancel your subscription? This will also disconnect your Stripe account. You will no longer be able to receive payments from customers."
				confirmLabel="Yes, Cancel Subscription"
				cancelLabel="Keep Subscription"
				loadingLabel="Cancelling..."
				isLoading={isCancelling}
				onConfirm={handleCancelSubscription}
				onCancel={() => setShowCancelConfirm(false)}
			/>
		</AppLayout>
	);
};

export default StaffPaymentSetup;
